import { TRPCClientError } from '@trpc/client'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAnal, useModal } from '../../../hooks'
import { EModalName } from '../../../types/enums'
import { trpc } from '../../../utils'
import { Button } from '../../atoms'
import { GameTypeTabs } from '../../molecules'
import { TableSettingsForm } from '..'
import { initialPrivateTableSettings } from './constants'
import { containerStyle, createGameButton } from './index.css'
import type { PrivateTableSettingsType } from './types'

export const CreatePrivateTable = (props: {
  onCreate: (table: { id: string; password: string | null }) => void
}) => {
  const { mutateAsync: createPrivateTable } =
    trpc.table.createPrivateTable.useMutation()
  const [privateTable, setPrivateTable] = useState<PrivateTableSettingsType>(
    initialPrivateTableSettings,
  )
  const { t } = useTranslation()
  const { openInfoModal } = useModal()
  const { logEvent } = useAnal()

  const onFieldChange = <GKey extends keyof PrivateTableSettingsType>(
    field: GKey,
    value: PrivateTableSettingsType[GKey],
  ) => {
    setPrivateTable((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const onClickCreateTable = async () => {
    try {
      const response = await createPrivateTable({
        ...privateTable,
        password: privateTable.password || null,
      })
      if (!response) return

      logEvent('table_created', {
        id: response.id,
        currency: response.currency,
      })

      props.onCreate({
        id: response.id,
        password: response.password,
      })
    } catch (error) {
      if (error instanceof TRPCClientError) {
        if (error.data.httpStatus === 409) {
          openInfoModal(
            {
              text: t('modal.info.privateTableLimit.text'),
              buttonText: t('modal.info.privateTableLimit.confirm'),
            },
            {
              title: t('modal.info.privateTableLimit.title'),
              name: EModalName.TOO_MUCH_PRIVATE_TABLES,
            },
          )
        }
      }
    }
  }

  return (
    <div className={containerStyle}>
      <GameTypeTabs
        gameType={privateTable.gameType}
        onChange={(type) => onFieldChange('gameType', type)}
      />
      <TableSettingsForm
        settings={privateTable}
        mode='create'
        onChange={onFieldChange}
      />
      <Button
        className={createGameButton}
        variant='primary'
        analytic={{
          name: 'create',
        }}
        onClick={() => {
          void onClickCreateTable()
        }}
      >
        {t('createPrivateGame.button.createGame')}
      </Button>
    </div>
  )
}
